import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";
import { ordersAnalyticsClientSelection } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";

export type DailyQueryState = {
    /** Workspace scope, carried as a comma-separated list. */
    scope: string[];
    types: string[];
    windowPreset: string | null;
    windowFrom: string | null;
    windowTo: string | null;
    queue: string | null;
    stage: string | null;
    /** B3: the Madrid calendar day (YYYY-MM-DD) of the `day` queue, and its filter. */
    date: string | null;
    filter: string | null;
    incidentId: string | null;
    operationId: string | null;
    page: number;
    cursor: string | null;
};

export type DailyQueryPatch = {
    scope?: string[];
    types?: string[];
    windowPreset?: string | null;
    windowFrom?: string | null;
    windowTo?: string | null;
    queue?: string | null;
    stage?: string | null;
    date?: string | null;
    filter?: string | null;
    incidentId?: string | null;
    operationId?: string | null;
    page?: number | null;
    cursor?: string | null;
    /** Operator free-text. Never leaves component state (see `set`). */
    search?: string;
};

export type DailyQueryNotice = "cursor_expired";

export type DailyQueryStateApi = {
    query: DailyQueryState;
    search: string;
    notice: DailyQueryNotice | null;
    set: (patch: DailyQueryPatch, options?: { replace?: boolean }) => void;
    resetPaginationForExpiredCursor: () => void;
    dismissNotice: () => void;
};

const readList = (params: URLSearchParams, name: string): string[] => {
    const raw = params.get(name);
    if (raw === null) return [];
    return raw.split(",").map((part) => part.trim()).filter((part) => part !== "");
};

const readText = (params: URLSearchParams, name: string): string | null => {
    const raw = params.get(name);
    if (raw === null || raw.trim() === "") return null;
    return raw;
};

const readPage = (params: URLSearchParams): number => {
    const raw = params.get("page");
    if (raw === null) return 1;
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed) || parsed < 1) return 1;
    return parsed;
};

const applyPatch = (current: URLSearchParams, patch: Record<string, string | null>): URLSearchParams => {
    // Tolerant schema: the next URL is built FROM the current one, so any parameter
    // this screen does not own survives untouched instead of being whitelisted away.
    const next = new URLSearchParams(current);
    Object.keys(patch).forEach((name) => {
        const value = patch[name];
        if (value === null || value === "") next.delete(name);
        else next.set(name, value);
    });
    return next;
};

export const useDailyQueryState = (): DailyQueryStateApi => {
    const [params, setParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [notice, setNotice] = useState<DailyQueryNotice | null>(null);

    const query: DailyQueryState = {
        // D114: a Daily URL without `scope` adopts the client picked anywhere in the app
        // (the legacy filter bar's session entry), so every screen shows the same one.
        scope: params.has("scope") ? readList(params, "scope") : ordersAnalyticsClientSelection.read(),
        types: readList(params, "types"),
        windowPreset: readText(params, "window"),
        windowFrom: readText(params, "from"),
        windowTo: readText(params, "to"),
        queue: readText(params, "queue"),
        stage: readText(params, "stage"),
        date: readText(params, "date"),
        filter: readText(params, "filter"),
        incidentId: readText(params, "incidentId"),
        operationId: readText(params, "operationId"),
        page: readPage(params),
        cursor: readText(params, "cursor"),
    };

    const set = useCallback((patch: DailyQueryPatch, options?: { replace?: boolean }): void => {
        const urlPatch: Record<string, string | null> = {};
        if (patch.scope !== undefined) {
            urlPatch.scope = patch.scope.join(",");
            // D114: the pick is shared with every other page of the app.
            ordersAnalyticsClientSelection.write(patch.scope);
        }
        if (patch.types !== undefined) urlPatch.types = patch.types.join(",");
        if (patch.windowPreset !== undefined) urlPatch.window = patch.windowPreset;
        if (patch.windowFrom !== undefined) urlPatch.from = patch.windowFrom;
        if (patch.windowTo !== undefined) urlPatch.to = patch.windowTo;
        if (patch.queue !== undefined) urlPatch.queue = patch.queue;
        if (patch.stage !== undefined) urlPatch.stage = patch.stage;
        if (patch.date !== undefined) urlPatch.date = patch.date;
        if (patch.filter !== undefined) urlPatch.filter = patch.filter;
        if (patch.incidentId !== undefined) urlPatch.incidentId = patch.incidentId;
        if (patch.operationId !== undefined) urlPatch.operationId = patch.operationId;
        if (patch.page !== undefined) urlPatch.page = patch.page === null || patch.page <= 1 ? null : String(patch.page);
        if (patch.cursor !== undefined) urlPatch.cursor = patch.cursor;
        // FR-025/SC-025: the operator search matches account emails. It stays in
        // component state and travels as a request header — never in a URL that can
        // be shared, bookmarked, or written to a proxy log.
        if (patch.search !== undefined) setSearch(patch.search);
        if (Object.keys(urlPatch).length === 0) return;
        setParams((current) => applyPatch(current, urlPatch), { replace: options?.replace === true });
    }, [setParams]);

    // Enmienda C8: the cursor is signed and expiring. When the backend rejects it,
    // the only honest recovery is page 1 plus a visible notice.
    const resetPaginationForExpiredCursor = useCallback((): void => {
        setNotice("cursor_expired");
        setParams((current) => applyPatch(current, { page: null, cursor: null }), { replace: true });
    }, [setParams]);

    const dismissNotice = useCallback((): void => setNotice(null), []);

    return { query, search, notice, set, resetPaginationForExpiredCursor, dismissNotice };
};
